import React, { useEffect } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MAX_STOCK_LIMIT } from '../../utils/cartRules';
import { useCartQuantityControl } from '../../hooks/cart/useCartQuantityControl';
import { useIsDesktop } from '@/hooks/useIsDesktop';

interface CartQuantityControlProps {
  lineRef: string;
  quantity: number;
  maxQty?: number;
  onOptimisticQtyChange?: (qty: number) => void;
  onRegisterCancel?: (cancel: () => void) => void;
  onRegisterPrepareForRemove?: (prepare: () => void) => void;
  onMinAttempt?: () => void;
}

export const CartQuantityControl: React.FC<CartQuantityControlProps> = ({
  lineRef,
  quantity,
  maxQty = MAX_STOCK_LIMIT,
  onOptimisticQtyChange,
  onRegisterCancel,
  onRegisterPrepareForRemove,
  onMinAttempt
}) => {
  const isDesktop = useIsDesktop();

  const {
    displayQty,
    inputValue,
    onIncrease,
    onDecrease,
    onInputChange,
    onFocus,
    onBlur,
    cancelPending,
    prepareForRemove
  } = useCartQuantityControl({
    lineRef,
    initialQty: quantity,
    maxQty
  });

  useEffect(() => {
    onOptimisticQtyChange?.(displayQty);
  }, [displayQty, onOptimisticQtyChange]);

  useEffect(() => () => {
    cancelPending();
  }, [cancelPending]);

  useEffect(() => {
    if (onRegisterCancel) {
      onRegisterCancel(cancelPending);
    }
  }, [cancelPending, onRegisterCancel]);

  useEffect(() => {
    if (onRegisterPrepareForRemove) {
      onRegisterPrepareForRemove(prepareForRemove);
    }
  }, [onRegisterPrepareForRemove, prepareForRemove]);

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const handleDecrease = () => {
    if (displayQty <= 1) {
      onMinAttempt?.();
      return;
    }
    onDecrease();
  };

  return (
    <div className="inline-flex items-center gap-1 h-10 w-32 sm:w-auto rounded-lg bg-gray-100 px-1 justify-between sm:justify-start">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-md hover:bg-white hover:shadow-sm text-gray-500 transition-all disabled:opacity-50"
        onClick={handleDecrease}
      >
        <Minus className="h-3 w-3" />
      </Button>

      {isDesktop ? (
        <input
          type="number"
          pattern="[0-9]*"
          inputMode="numeric"
          id={`cart-qty-${lineRef}`}
          name={`quantity-${lineRef}`}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onBlur={onBlur}
          onKeyDown={handleKeyDown}
          onFocus={onFocus}
          min="1"
          max={maxQty}
          className="w-[3ch] bg-transparent text-center text-sm font-semibold text-gray-900 tabular-nums border-none h-7 focus:ring-0 focus:outline-none focus-visible:outline-none focus-visible:ring-0"
          style={{ textAlign: 'center', fontSize: '14px' }}
          aria-label="Quantity"
        />
      ) : (
        <span className="w-[3ch] text-center text-sm font-semibold text-gray-900 tabular-nums select-none">
          {displayQty}
        </span>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-md hover:bg-white hover:shadow-sm text-gray-500 transition-all disabled:opacity-50"
        onClick={onIncrease}
      >
        <Plus className="h-3 w-3" />
      </Button>
    </div>
  );
};
